import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import {
  db,
  PaletteRecord,
  PaletteHistoryRecord,
} from "../lib/database";
import { colorEngine, HarmonyType } from "../lib/colorEngine";
import { ShuffleOptionsPanel } from "./palette/ShuffleOptionsPanel";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
  DragOverlay,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
} from "@dnd-kit/sortable";
import {
  ColorInput,
} from "./ui/ColorInput";
import { SortableColorSwatch } from "./SortableColorSwatch";
import {
  ChevronDown,
  ChevronRight,
  Info,
  Pipette,
  Plus,
  Settings2,
  Shuffle,
  XCircle,
} from "lucide-react";
import { ExportModal } from "./ExportModal";
import { ImportModal } from "./ImportModal";
import { PaletteComparisonModal } from "./PaletteComparisonModal";
import { PaletteTagManager } from "./PaletteTagManager";
import { ActivityFeedPanel } from "./ActivityFeedPanel";
import { PalettePermissionsPanel } from "./PalettePermissionsPanel";
import { PaletteRoleBadge } from "./PaletteRoleBadge";
import { FitnessScorePanel } from "./FitnessScorePanel";
import { useEyeDropper } from "../hooks/useEyeDropper";
import { usePalettePermissions } from "../hooks/usePalettePermissions";
import { useKeyboardShortcutsContext } from "../contexts/KeyboardShortcutsContext";
import { tokenEngineService } from "../lib/tokenEngineIntegration";
import { PaletteList } from "./palette/PaletteList";
import { PaletteToolbar } from "./palette/PaletteToolbar";
import { usePaletteContext } from "../contexts/PaletteContext";

const EMPTY_COLORS: string[] = [];

export function PaletteEditor({ contextHex }: { contextHex?: string }) {
  const {
    palettes,
    currentPalette,
    setCurrentPalette,
    updateCurrentPalette,
    refreshPalettes,
  } = usePaletteContext();
  const [filteredPalettes, setFilteredPalettes] = useState<PaletteRecord[]>([]);
  const [paletteName, setPaletteName] = useState("");
  const [newColorInput, setNewColorInput] = useState("");
  const [showExportModal, setShowExportModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);

  const [addColorError, setAddColorError] = useState<string | null>(null);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editNameValue, setEditNameValue] = useState('');
  const [createError, setCreateError] = useState<string | null>(null);

  const {
    isSupported: eyeDropperSupported,
    open: openEyeDropper,
    error: eyeDropperError,
  } = useEyeDropper();
  const [paletteHistory, setPaletteHistory] = useState<PaletteHistoryRecord[]>(
    [],
  );
  const [showHistoryPanel, setShowHistoryPanel] = useState(false);
  const [showActivityPanel, setShowActivityPanel] = useState(false);
  const [showPermissionsPanel, setShowPermissionsPanel] = useState(false);
  const [showFitnessPanel, setShowFitnessPanel] = useState(false);
  const [showTagsSection, setShowTagsSection] = useState(false);
  const [isGeneratingTokens, setIsGeneratingTokens] = useState(false);
  const [selectedComparisonVersion, setSelectedComparisonVersion] =
    useState<PaletteHistoryRecord | null>(null);

  // Shuffle constraints state
  const [showShuffleOptions, setShowShuffleOptions] = useState(false);
  const [shuffleHarmonyMode, setShuffleHarmonyMode] = useState<
    HarmonyType | undefined
  >(undefined);
  const [shuffleHueMin, setShuffleHueMin] = useState<number | undefined>(
    undefined,
  );
  const [shuffleHueMax, setShuffleHueMax] = useState<number | undefined>(
    undefined,
  );
  const [shuffleLightnessMin, setShuffleLightnessMin] = useState<
    number | undefined
  >(undefined);
  const [shuffleLightnessMax, setShuffleLightnessMax] = useState<
    number | undefined
  >(undefined);
  const [shuffleSaturationMin, setShuffleSaturationMin] = useState<
    number | undefined
  >(undefined);
  const [shuffleSaturationMax, setShuffleSaturationMax] = useState<
    number | undefined
  >(undefined);

  // Drag and drop state
  const [activeId, setActiveId] = useState<string | null>(null);

  const [colorOrder, setColorOrder] = useState<string[]>(currentPalette?.colors || []);

  // Get permissions for current palette
  const permissions = usePalettePermissions(currentPalette?.id || null);

  // Keyboard shortcuts
  const { registerShortcuts, unregisterShortcuts, setModalOpen } =
    useKeyboardShortcutsContext();

  // Configure drag-and-drop sensors with touch support
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Require 8px movement before drag starts
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 250, // Long-press for 250ms to start dragging on touch devices
        tolerance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const paletteColors = currentPalette?.colors ?? EMPTY_COLORS;

  const sortableIds = useMemo(
    () => colorOrder.map((color, i) => `${i}:${color}`),
    [colorOrder]
  );

  const loadPalettes = useCallback(() => refreshPalettes(), [refreshPalettes]);

  useEffect(() => {
    loadPalettes();
  }, [loadPalettes]);

  // Initialize filtered palettes when palettes change
  useEffect(() => {
    setFilteredPalettes(palettes);
  }, [palettes]);

  // Get all available tags from all palettes
  const availableTags = useMemo(() => {
    const tagSet = new Set<string>();
    palettes.forEach((palette) => {
      palette.tags?.forEach((tag) => tagSet.add(tag));
    });
    return Array.from(tagSet).sort();
  }, [palettes]);

  useEffect(() => {
    const loadHistory = async () => {
      if (currentPalette?.id) {
        const history = await db.getPaletteHistory(currentPalette.id);
        setPaletteHistory(history);
      } else {
        setPaletteHistory([]);
      }
    };
    loadHistory();
  }, [currentPalette?.id]);

  const paletteColorsJson = JSON.stringify(currentPalette?.colors ?? []);
  const paletteId = currentPalette?.id;

  // Sync colorOrder with currentPalette changes
  useEffect(() => {
    if (paletteId && currentPalette?.colors) {
      setColorOrder(currentPalette.colors);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paletteId, paletteColorsJson]);

  // Drag and drop handlers
  const handleDragStart = (event: DragStartEvent) => {
    const compositeId = event.active.id as string;
    const color = compositeId.substring(compositeId.indexOf(':') + 1);
    setActiveId(color);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over || !currentPalette) return;

    if (active.id !== over.id) {
      const oldIndex = sortableIds.indexOf(active.id as string);
      const newIndex = sortableIds.indexOf(over.id as string);

      if (oldIndex !== -1 && newIndex !== -1) {
        const newOrder = arrayMove(colorOrder, oldIndex, newIndex);
        setColorOrder(newOrder); // This will add to undo history
        await updateCurrentPalette({ colors: newOrder });
      }
    }
  };

  // Keyboard-based reordering (Alt+Arrow keys)
  const handleKeyboardMove = async (
    index: number,
    direction: "left" | "right",
  ) => {
    if (!currentPalette) return;

    const newIndex = direction === "left" ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= colorOrder.length) return;

    const newOrder = arrayMove(colorOrder, index, newIndex);
    setColorOrder(newOrder); // This will add to undo history
    await updateCurrentPalette({ colors: newOrder });
  };

  const prevColorsRef = useRef<string[]>(currentPalette?.colors ?? []);
  useEffect(() => {
    if (!currentPalette) return;
    const prev = prevColorsRef.current;
    prevColorsRef.current = currentPalette.colors;
    const userChanged = JSON.stringify(colorOrder) !== JSON.stringify(prev);
    const dbMismatch = JSON.stringify(colorOrder) !== JSON.stringify(currentPalette.colors);
    if (userChanged && dbMismatch && colorOrder.length > 0) {
      updateCurrentPalette({ colors: colorOrder });
    }
  }, [colorOrder, currentPalette]); // eslint-disable-line react-hooks/exhaustive-deps

  const createNewPalette = async () => {
    setCreateError(null);
    const name = paletteName.trim() || `Palette ${palettes.length + 1}`;
    const id = await db.addPalette({
      name,
      colors: [],
      tags: [],
    });

    await loadPalettes();
    const newPalette = await db.getPalette(id);
    setCurrentPalette(newPalette || null);
    setPaletteName("");
  };

  const createFromHarmony = async (colors: string[], autoName: string) => {
    setCreateError(null);
    const name = paletteName.trim() || autoName;
    const id = await db.addPalette({
      name,
      colors,
      tags: [],
    });

    await loadPalettes();
    const newPalette = await db.getPalette(id);
    setCurrentPalette(newPalette || null);
    setPaletteName("");
  };

  const addColorToPalette = () => {
    if (!currentPalette) return;

    if (!newColorInput.trim()) {
      setAddColorError("Enter a color value");
      return;
    }

    const parsed = colorEngine.parseColor(newColorInput);
    if (!parsed) {
      setAddColorError("Invalid color format");
      return;
    }

    setAddColorError(null);
    const updatedColors = [...currentPalette.colors, parsed.hex];
    updateCurrentPalette({ colors: updatedColors });
    setNewColorInput("");
  };

  const removeColor = (index: number) => {
    if (!currentPalette) return;

    const updatedColors = currentPalette.colors.filter((_, i) => i !== index);
    updateCurrentPalette({ colors: updatedColors });
  };

  const toggleColorLock = async (index: number) => {
    if (!currentPalette) return;

    const lockedColors = currentPalette.lockedColors || [];
    const isLocked = lockedColors.includes(index);

    const updatedLocked = isLocked
      ? lockedColors.filter((i) => i !== index)
      : [...lockedColors, index];

    await updateCurrentPalette({ lockedColors: updatedLocked });
  };

  const shuffleColors = () => {
    if (!currentPalette) return;

    const options: Parameters<typeof colorEngine.shuffleColors>[1] = {
      lockedIndices: currentPalette.lockedColors || [],
    };

    // Add harmony constraint if selected
    if (shuffleHarmonyMode) {
      options.harmonyType = shuffleHarmonyMode;
    }

    // Add hue range constraint if specified
    if (shuffleHueMin !== undefined && shuffleHueMax !== undefined) {
      options.hueRange = { min: shuffleHueMin, max: shuffleHueMax };
    }

    // Add lightness range constraint if specified
    if (
      shuffleLightnessMin !== undefined &&
      shuffleLightnessMax !== undefined
    ) {
      options.lightnessRange = {
        min: shuffleLightnessMin,
        max: shuffleLightnessMax,
      };
    }

    // Add saturation range constraint if specified
    if (
      shuffleSaturationMin !== undefined &&
      shuffleSaturationMax !== undefined
    ) {
      options.saturationRange = {
        min: shuffleSaturationMin,
        max: shuffleSaturationMax,
      };
    }

    const shuffled = colorEngine.shuffleColors(currentPalette.colors, options);
    updateCurrentPalette({ colors: shuffled });
  };

  const deletePalette = async (id: string) => {
    await db.deletePalette(id);
    if (currentPalette?.id === id) {
      setCurrentPalette(null);
    }
    await loadPalettes();
  };

  const generateTokensFromCurrentPalette = async () => {
    if (!currentPalette?.id) return;

    setIsGeneratingTokens(true);
    try {
      const result = await tokenEngineService.generateTokensFromPalette(
        currentPalette.id,
        {
          namingConvention: "systematic",
          includeSemanticTokens: true,
          includeAliases: true,
        },
      );

      if (!result.validation.valid) {
        console.error("Token validation failed:", result.validation.errors);
        // In real implementation, show error to user
        return;
      }

      // Create download for the generated tokens
      const exportResult = await tokenEngineService.exportTokens(
        result.tokenSet,
        {
          platform: "css",
          includeMetadata: true,
          includeComments: true,
          minify: false,
          namingConvention: "kebab-case",
        },
      );

      // Create download
      const blob = new Blob([exportResult.content], { type: "text/css" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = exportResult.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      // Log token generation activity
      if (currentPalette.id) {
        await db.logActivity({
          type: "palette_exported",
          paletteId: currentPalette.id,
          paletteName: currentPalette.name,
          description: `Generated design tokens from palette "${currentPalette.name}"`,
          details: {
            exportFormat: "css",
            tokenCount: exportResult.tokens,
          },
        });
      }
    } catch (error) {
      console.error("Failed to generate tokens:", error);
      // In real implementation, show error to user
    } finally {
      setIsGeneratingTokens(false);
    }
  };

  const handleImportPalette = async (
    colors: string[],
    name?: string,
    format?: string,
  ) => {
    if (currentPalette) {
      // Import into existing palette
      await updateCurrentPalette({ colors });

      // Log import activity
      if (currentPalette.id) {
        await db.logActivity({
          type: "palette_imported",
          paletteId: currentPalette.id,
          paletteName: currentPalette.name,
          description: `Imported ${colors.length} colors into palette "${currentPalette.name}"${format ? ` from ${format}` : ""}`,
          details: {
            importFormat: format,
            colorCount: colors.length,
          },
        });
      }
    } else if (name) {
      // Create new palette with imported colors
      const id = await db.addPalette({
        name,
        colors,
        tags: [],
      });
      await loadPalettes();
      const newPalette = await db.getPalette(id);
      setCurrentPalette(newPalette || null);

      // Log import activity (creation is already logged in addPalette)
      if (format) {
        await db.logActivity({
          type: "palette_imported",
          paletteId: id,
          paletteName: name,
          description: `Imported palette "${name}" from ${format} with ${colors.length} colors`,
          details: {
            importFormat: format,
            colorCount: colors.length,
          },
        });
      }
    }
  };

  const handleTagsChange = async (tags: string[]) => {
    if (!currentPalette) return;
    await updateCurrentPalette({ tags });
  };

  // Register keyboard shortcuts
  useEffect(() => {
    const shortcuts = [
      {
        key: "s",
        ctrlOrCmd: true,
        description: "Save current palette",
        category: "Palette",
        handler: () => {
          // Palette is auto-saved on changes, so we just show a brief confirmation
          if (currentPalette) {
            // Could add a toast notification here in the future
            console.log("Palette saved:", currentPalette.name);
          }
        },
      },
      {
        key: "Escape",
        description: "Close modals and panels",
        category: "General",
        handler: () => {
          if (showExportModal) {
            setShowExportModal(false);
            setModalOpen(false);
          } else if (showImportModal) {
            setShowImportModal(false);
            setModalOpen(false);
          } else if (selectedComparisonVersion) {
            setSelectedComparisonVersion(null);
            setModalOpen(false);
          }
        },
      },
    ];

    registerShortcuts(shortcuts);

    return () => {
      unregisterShortcuts(shortcuts.map((s) => s.key));
    };
  }, [
    currentPalette,
    showExportModal,
    showImportModal,
    selectedComparisonVersion,
    registerShortcuts,
    unregisterShortcuts,
    setModalOpen,
  ]);

  // Track modal state for keyboard shortcuts
  useEffect(() => {
    const hasModalOpen =
      showExportModal || showImportModal || selectedComparisonVersion !== null;
    setModalOpen(hasModalOpen);
  }, [
    showExportModal,
    showImportModal,
    selectedComparisonVersion,
    setModalOpen,
  ]);

  return (
    <div className="w-full max-w-6xl mx-auto p-6">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* 5G — Palette List */}
        <PaletteList
          palettes={filteredPalettes}
          currentPalette={currentPalette}
          paletteName={paletteName}
          createError={createError}
          onPaletteNameChange={setPaletteName}
          onCreateErrorChange={setCreateError}
          onSelect={setCurrentPalette}
          onCreate={createNewPalette}
          onCreateFromHarmony={createFromHarmony}
          onDelete={deletePalette}
          onFilteredPalettesChange={setFilteredPalettes}
          contextHex={contextHex}
        />

        <div className="lg:col-span-2">
          {currentPalette ? (
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
              <div className="flex justify-between items-center mb-4">
                <div className="flex items-center gap-3">
                  {isEditingName ? (
                    <input
                      autoFocus
                      value={editNameValue}
                      onChange={(e) => setEditNameValue(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          const trimmed = editNameValue.trim();
                          if (trimmed && trimmed !== currentPalette.name) {
                            updateCurrentPalette({ name: trimmed });
                          }
                          setIsEditingName(false);
                        }
                        if (e.key === 'Escape') {
                          setIsEditingName(false);
                        }
                      }}
                      onBlur={() => {
                        const trimmed = editNameValue.trim();
                        if (trimmed && trimmed !== currentPalette.name) {
                          updateCurrentPalette({ name: trimmed });
                        }
                        setIsEditingName(false);
                      }}
                      className="text-2xl font-bold text-gray-900 dark:text-gray-100 bg-transparent border-b-2 border-blue-500 outline-none"
                    />
                  ) : (
                    <h2
                      className="text-2xl font-bold text-gray-900 dark:text-gray-100 cursor-pointer hover:underline decoration-dotted underline-offset-4"
                      title="Click to rename"
                      onClick={() => {
                        setEditNameValue(currentPalette.name);
                        setIsEditingName(true);
                      }}
                    >
                      {currentPalette.name}
                    </h2>
                  )}
                  <PaletteRoleBadge
                    role={permissions.userRole}
                    isPublic={permissions.access?.isPublic}
                  />
                </div>
              </div>

              {/* ── 1. Add Color Input (primary interaction — always first) ── */}
              <div className="flex gap-2 mb-4 pb-4 border-b border-gray-200 dark:border-gray-600">
                <ColorInput
                  value={newColorInput}
                  onChange={(value: string) => {
                    setNewColorInput(value);
                    if (addColorError) setAddColorError(null);
                  }}
                  placeholder="Add color (hex, rgb, hsl…)"
                />
                <button
                  onClick={addColorToPalette}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  <span className="hidden sm:inline">Add</span>
                </button>
                {eyeDropperSupported && (
                  <button
                    onClick={async () => {
                      const color = await openEyeDropper();
                      if (color && currentPalette) {
                        updateCurrentPalette({
                          colors: [...currentPalette.colors, color],
                        });
                        setNewColorInput(color);
                      }
                    }}
                    className="px-3 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-900 dark:text-gray-100 rounded-lg transition-colors"
                    title="Pick color from screen"
                  >
                    <Pipette className="w-4 h-4" />
                  </button>
                )}
                {contextHex && currentPalette && (
                  <button
                    onClick={() => {
                      updateCurrentPalette({
                        colors: [...currentPalette.colors, contextHex],
                      });
                      setNewColorInput(contextHex);
                    }}
                    className="px-3 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-900 dark:text-gray-100 rounded-lg transition-colors flex items-center gap-2"
                    title={`Add active color: ${contextHex}`}
                  >
                    <div
                      className="w-4 h-4 rounded border border-gray-300 dark:border-gray-500"
                      style={{ backgroundColor: contextHex }}
                    />
                    <span className="hidden sm:inline text-xs">Active</span>
                  </button>
                )}
              </div>
              {addColorError && <p className="text-red-500 text-xs mt-1 -mt-3 mb-3">{addColorError}</p>}
              {eyeDropperError && (
                <p className="text-xs text-red-500 -mt-3 mb-3">{eyeDropperError}</p>
              )}
              {!eyeDropperSupported && (
                <p className="text-xs text-gray-500 dark:text-gray-400 -mt-3 mb-3">
                  <Info className="w-3 h-3 inline mr-1" />
                  EyeDropper is supported in Chrome, Edge, and Opera.
                </p>
              )}

              {/* ── 2. Sortable Color Swatches (primary content) ── */}
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={sortableIds}
                  strategy={rectSortingStrategy}
                >
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                    {colorOrder.map((color, index) => (
                      <SortableColorSwatch
                        key={sortableIds[index]}
                        id={sortableIds[index]}
                        color={color}
                        index={index}
                        onRemove={removeColor}
                        onKeyboardMove={handleKeyboardMove}
                        onToggleLock={toggleColorLock}
                        isLocked={currentPalette?.lockedColors?.includes(index)}
                        isReadOnly={!permissions.canEdit}
                      />
                    ))}
                  </div>
                </SortableContext>
                <DragOverlay>
                  {activeId ? (
                    <div
                      className="w-24 h-24 rounded-xl border-4 border-white shadow-2xl"
                      style={{ backgroundColor: activeId }}
                    />
                  ) : null}
                </DragOverlay>
              </DndContext>

              {/* Shuffle controls */}
              {colorOrder.length > 0 && (
                <div className="flex items-center gap-2 mt-4">
                  <button
                    onClick={shuffleColors}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-900 dark:text-gray-100 rounded-lg text-sm transition-colors"
                  >
                    <Shuffle className="w-3.5 h-3.5" />
                    <span>Shuffle</span>
                  </button>
                  <button
                    onClick={() => setShowShuffleOptions(!showShuffleOptions)}
                    className={`inline-flex items-center justify-center w-8 h-8 rounded-lg transition-colors ${
                      showShuffleOptions
                        ? "bg-blue-600 text-white"
                        : "bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-500 dark:text-gray-400"
                    }`}
                    title="Shuffle options"
                  >
                    <Settings2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}

              {showShuffleOptions && colorOrder.length > 0 && (
                <ShuffleOptionsPanel
                  harmonyMode={shuffleHarmonyMode}
                  hueMin={shuffleHueMin}
                  hueMax={shuffleHueMax}
                  lightnessMin={shuffleLightnessMin}
                  lightnessMax={shuffleLightnessMax}
                  saturationMin={shuffleSaturationMin}
                  saturationMax={shuffleSaturationMax}
                  onHarmonyModeChange={setShuffleHarmonyMode}
                  onHueMinChange={setShuffleHueMin}
                  onHueMaxChange={setShuffleHueMax}
                  onLightnessMinChange={setShuffleLightnessMin}
                  onLightnessMaxChange={setShuffleLightnessMax}
                  onSaturationMinChange={setShuffleSaturationMin}
                  onSaturationMaxChange={setShuffleSaturationMax}
                  onReset={() => {
                    setShuffleHarmonyMode(undefined);
                    setShuffleHueMin(undefined);
                    setShuffleHueMax(undefined);
                    setShuffleLightnessMin(undefined);
                    setShuffleLightnessMax(undefined);
                    setShuffleSaturationMin(undefined);
                    setShuffleSaturationMax(undefined);
                  }}
                />
              )}

              {/* ── 3. Tags (collapsible, collapsed by default) ── */}
              <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-600">
                <button
                  onClick={() => setShowTagsSection(!showTagsSection)}
                  className="flex items-center gap-1.5 text-sm font-semibold text-gray-900 dark:text-gray-100 hover:text-blue-600 dark:hover:text-blue-400 transition-colors w-full text-left"
                >
                  {showTagsSection ? (
                    <ChevronDown className="w-3.5 h-3.5" />
                  ) : (
                    <ChevronRight className="w-3.5 h-3.5" />
                  )}
                  Tags
                  {(currentPalette.tags?.length ?? 0) > 0 && (
                    <span className="text-xs font-normal text-gray-500 dark:text-gray-400">
                      ({currentPalette.tags!.length})
                    </span>
                  )}
                </button>
                {showTagsSection && (
                  <div className="mt-2">
                    <PaletteTagManager
                      tags={currentPalette.tags || []}
                      availableTags={availableTags}
                      onTagsChange={handleTagsChange}
                    />
                  </div>
                )}
              </div>

              {/* ── 4. Fitness Scoring (collapsible, collapsed by default) ── */}
              <div className="mt-3 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                <button
                  onClick={() => setShowFitnessPanel(!showFitnessPanel)}
                  className="flex items-center gap-1.5 text-sm font-semibold text-gray-900 dark:text-gray-100 hover:text-purple-600 dark:hover:text-purple-400 transition-colors w-full text-left"
                >
                  {showFitnessPanel ? (
                    <ChevronDown className="w-3.5 h-3.5" />
                  ) : (
                    <ChevronRight className="w-3.5 h-3.5" />
                  )}
                  Palette Fitness Scoring
                </button>
                {showFitnessPanel && (
                  <div className="mt-3">
                    <FitnessScorePanel colors={paletteColors} />
                  </div>
                )}
                {!showFitnessPanel && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 ml-5">
                    Evaluate fitness for dark UI, data visualization, and more.
                  </p>
                )}
              </div>

              {/* ── 5. Toolbar ── */}
              <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-600">
                <PaletteToolbar
                  paletteHistoryCount={paletteHistory.length}
                  isGeneratingTokens={isGeneratingTokens}
                  onShowPermissions={() => setShowPermissionsPanel(!showPermissionsPanel)}
                  onShowActivity={() => setShowActivityPanel(!showActivityPanel)}
                  onShowHistory={() => setShowHistoryPanel(!showHistoryPanel)}
                  onShowImport={() => setShowImportModal(true)}
                  onShowExport={() => setShowExportModal(true)}
                  onGenerateTokens={generateTokensFromCurrentPalette}
                />
              </div>

            </div>
          ) : (
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6 text-center">
              <p className="text-gray-500 dark:text-gray-400">
                Select or create a palette to get started
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Panels & Modals */}
      {showPermissionsPanel && currentPalette?.id && (
        <PalettePermissionsPanel
          paletteId={currentPalette.id}
          onClose={() => setShowPermissionsPanel(false)}
        />
      )}

      {showActivityPanel && currentPalette?.id && (
        <ActivityFeedPanel
          paletteId={currentPalette.id}
        />
      )}

      {showHistoryPanel && currentPalette && (
        <div
          className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
        >
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-2xl w-full max-w-2xl mx-4 max-h-[80vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Version History ({paletteHistory.length})
              </h3>
              <button
                onClick={() => setShowHistoryPanel(false)}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                <XCircle className="w-5 h-5" />
              </button>
            </div>
            {paletteHistory.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                No version history available.
              </p>
            ) : (
              <div className="space-y-3">
                {paletteHistory.map((version) => (
                  <div
                    key={version.id}
                    className="p-3 border border-gray-200 dark:border-gray-700 rounded-lg"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {new Date(version.timestamp).toLocaleString()}
                      </span>
                      <div className="flex gap-2">
                        <button
                          onClick={() => {
                            setSelectedComparisonVersion(version);
                            setShowHistoryPanel(false);
                          }}
                          className="text-xs px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded"
                        >
                          Compare
                        </button>
                      </div>
                    </div>
                    <div className="flex gap-1">
                      {version.colors.slice(0, 8).map((color, i) => (
                        <div
                          key={i}
                          className="w-5 h-5 rounded border border-gray-200 dark:border-gray-600"
                          style={{ backgroundColor: color }}
                        />
                      ))}
                      {version.colors.length > 8 && (
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          +{version.colors.length - 8}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {showImportModal && (
        <ImportModal
          onImport={handleImportPalette}
          onClose={() => setShowImportModal(false)}
        />
      )}

      {showExportModal && currentPalette && (
        <ExportModal
          palette={currentPalette}
          onClose={() => setShowExportModal(false)}
        />
      )}

      {selectedComparisonVersion && currentPalette && (
        <PaletteComparisonModal
          currentPalette={currentPalette}
          comparisonVersion={selectedComparisonVersion}
          onClose={() => setSelectedComparisonVersion(null)}
        />
      )}
    </div>
  );
}
